import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  FileUploadComponent,
  UploadStyles,
} from 'src/app/private/shared/components/file-management/file-upload/file-upload.component';
import { UploadType } from 'src/app/private/shared/enums/upload-type.enum';
import { FileDTO } from '../../dtos/file.dto';
import {
  ChatMessage,
  Conversation,
  GlaiService,
} from '../../services/glai/glai.service';

@Component({
  selector: 'app-glai',
  templateUrl: './glai.component.html',
  styleUrl: './glai.component.scss',
  standalone: false,
})
export class GlaiComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  @ViewChild('uploadComponent') uploadComponent: FileUploadComponent;

  @Output() readonly fileUploads = new EventEmitter<FileDTO>();

  uploadedFiles: FileDTO[] = [];

  uploadStyle = UploadStyles.ICON;

  uploadType = UploadType.USER_UPLOAD;

  activeModes: string[] = ['default'];

  userMessage = '';

  messages: ChatMessage[] = [];

  currentConversation: Conversation | null = null;

  isLoading = false;

  private messageSubscription?: Subscription;

  private shouldScrollToBottom = false;

  constructor(private glaiService: GlaiService) {}

  ngOnInit(): void {
    this.messageSubscription = this.glaiService.messages$.subscribe(
      (message: ChatMessage) => {
        const existingIndex = this.messages.findIndex(
          (m) => m.id === message.id
        );
        if (existingIndex >= 0) {
          this.messages[existingIndex] = message;
        } else {
          this.messages.push(message);
        }
        this.shouldScrollToBottom = true;
      }
    );
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.messageSubscription?.unsubscribe();
    this.glaiService.disconnectStream();
  }

  toggleMode(mode: string): void {
    /* const index = this.activeModes.indexOf(mode);
    if (index > -1) {
      this.activeModes.splice(index, 1);
    } else {
      this.activeModes.push(mode);
    } */
    this.activeModes = [mode];
  }

  sendMessage(): void {
    if (!this.userMessage.trim() || this.isLoading) return;

    const message = this.userMessage.trim();
    this.userMessage = '';
    this.isLoading = true;

    // Add user message to the chat
    const messageFiles = [...this.uploadedFiles];
    this.glaiService.addUserMessage(message, messageFiles);

    if (!this.currentConversation) {
      this.startNewConversation(message, messageFiles);
    } else {
      this.continueConversation(message, messageFiles);
    }

    // remove files
    this.uploadedFiles = [];
  }

  private startNewConversation(
    message: string,
    messageFiles?: FileDTO[]
  ): void {
    this.glaiService
      .startConversation(message, messageFiles, this.activeModes)
      .subscribe({
        next: (response) => {
          this.currentConversation = response.item;
          this.glaiService.connectToStream(response.item.glai_stream_id);
          this.isLoading = false;
        },
        error: (error) => {
          // eslint-disable-next-line no-console
          console.error('Error starting conversation:', error);
          this.isLoading = false;
        },
      });
  }

  private continueConversation(
    message: string,
    messageFiles?: FileDTO[]
  ): void {
    if (!this.currentConversation) return;

    this.glaiService
      .continueConversation(
        this.currentConversation.id,
        message,
        messageFiles,
        this.activeModes
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
        },
        error: (error) => {
          // eslint-disable-next-line no-console
          console.error('Error continuing conversation:', error);
          this.isLoading = false;
        },
      });
  }

  addToFileList(files: FileDTO): void {
    // add to uploadedFileIds
    this.uploadedFiles.push(files);
  }

  removeFromFileList(file: FileDTO) {
    // remove from uploadedFiles
    this.uploadedFiles = this.uploadedFiles.filter((f) => f.id !== file.id);
  }

  private scrollToBottom(): void {
    try {
      this.messagesContainer.nativeElement.scrollTop =
        this.messagesContainer.nativeElement.scrollHeight;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('unable to scroll');
    }
  }
}
